import { Box, Skeleton, Stack } from '@mui/material'
import PropTypes from 'prop-types'

function FormSection({ children }) {
  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: '50rem',
        gap: '1.5rem',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '.5rem',
        p: '1.5rem',
        bgcolor: '#FFF',
      }}
    >
      {children}
    </Stack>
  )
}

FormSection.propTypes = {
  children: PropTypes.node.isRequired,
}

function FieldSkeleton({ labelWidth }) {
  return (
    <Stack gap=".25rem">
      <Skeleton variant="text" width={labelWidth} height={20} />
      <Skeleton variant="rounded" height={40} />
    </Stack>
  )
}

FieldSkeleton.propTypes = {
  labelWidth: PropTypes.string,
}

FieldSkeleton.defaultProps = {
  labelWidth: '40%',
}

function IconRowSkeleton() {
  return (
    <Stack direction="row" gap="1rem" alignItems="center">
      <Skeleton variant="circular" width={20} height={20} />
      <Skeleton variant="text" width="60%" height={18} />
    </Stack>
  )
}

export function WhatsappProfileSkeleton() {
  return (
    <Stack
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '90rem',
        gap: '2rem',
        px: { xs: 0, lg: '11rem' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Skeleton variant="text" width="40%" height={40} />
        <Stack direction="row" gap="1rem">
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={80} height={36} />
        </Stack>
      </Box>

      {/* Content */}
      <Box
        sx={{
          width: '100%',
          height: 'calc(100% - 5rem)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: '.5rem',
        }}
      >
        {/* Form — 70% */}
        <Stack sx={{ width: '70%', overflowY: 'auto', bgcolor: '#FFF', pr: '.5rem', mb: '.5rem' }}>
          <FormSection>
            {/* Section title */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Skeleton variant="text" width="35%" height={28} />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>

            {/* Profile uploader */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Skeleton variant="text" width="20%" height={22} />
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton
                variant="rounded"
                height={100}
                sx={{ borderRadius: '9px', border: '1px dashed #ccc', mt: '12px' }}
              />
            </Box>

            {/* Username */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Skeleton variant="text" width="25%" height={22} />
              <Skeleton variant="text" width="65%" height={18} />
              <Stack direction="row" gap="1rem" alignItems="center">
                <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
                <Skeleton variant="rounded" width={120} height={40} />
              </Stack>
            </Box>

            {/* Info section (gray bg) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Skeleton variant="text" width="28%" height={22} />
              <Skeleton variant="text" width="75%" height={18} />
              <Stack
                sx={{
                  bgcolor: '#F5F5F5',
                  borderRadius: '0.5rem',
                  p: '1rem 2rem',
                  gap: '1.5rem',
                }}
              >
                <FieldSkeleton labelWidth="30%" />
                <Skeleton variant="rounded" height={120} />
                <FieldSkeleton labelWidth="20%" />
              </Stack>
            </Box>

            {/* Contact section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <Skeleton variant="text" width="22%" height={22} />
              <Skeleton variant="text" width="55%" height={18} />
              <Stack gap="1.5rem" sx={{ mt: '.5rem' }}>
                <FieldSkeleton labelWidth="18%" />
                <FieldSkeleton labelWidth="14%" />
                <FieldSkeleton labelWidth="16%" />
              </Stack>
            </Box>
          </FormSection>
        </Stack>

        {/* Preview — 30% */}
        <Stack
          sx={{
            width: '30%',
            maxHeight: '43rem',
            maxWidth: '25rem',
            overflowY: 'auto',
            borderRadius: '.5rem',
            bgcolor: '#F5F5F5',
            gap: '2rem',
            mb: '.5rem',
          }}
        >
          {/* Preview title */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '1.5rem', height: '3.75rem' }}>
            <Skeleton variant="text" width="55%" height={28} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.8rem', p: '1rem' }}>
            {/* Avatar + name */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <Skeleton variant="circular" width={80} height={80} />
              <Stack alignItems="center" gap=".25rem">
                <Skeleton variant="text" width={120} height={22} />
                <Skeleton variant="text" width={90} height={18} />
              </Stack>
            </Box>

            {/* Info rows */}
            <Stack sx={{ gap: '1.5rem' }}>
              <IconRowSkeleton />
              <IconRowSkeleton />
              <IconRowSkeleton />
              <IconRowSkeleton />
              <IconRowSkeleton />
            </Stack>

            {/* Phone number */}
            <Stack gap=".5rem">
              <Skeleton variant="text" width="50%" height={22} />
              <Skeleton variant="text" width="65%" height={18} />
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}
